import type { AppRouterInputs, AppRouterOutputs } from '@alison/api/router';

const POSITIONS = /^\s*\d+(\s*,\s*\d+)*\s*$/;
const MAX_POSITION = 100;

const FREQUENCY_LABEL: Record<Frequency, string> = {
  once: 'Once',
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
};

// Weekly runs on Mondays and monthly on the first Monday (lib/scheduling/slots.ts).
const FREQUENCY_DAY: Record<Frequency, string | null> = {
  once: null,
  daily: null,
  weekly: 'Mondays',
  monthly: '1st Monday',
};

// Codes after "channel_unavailable:" (notifications/dispatch.ts CHANNEL_UNAVAILABLE_ERRORS).
const CHANNEL_UNAVAILABLE_LABEL: Record<string, string> = {
  channel_not_found: 'channel not found',
  is_archived: 'channel archived',
  not_in_channel: 'Alison was removed from the channel',
};

// Only these finish with a delivery attempt; the rest show "—".
const DELIVERED_STATUSES = new Set(['completed', 'empty']);

type Draft = Extract<AppRouterOutputs['automations']['draft'], { automatable: true }>;
type Occurrence = AppRouterOutputs['automations']['deliveryLog']['occurrences'][number];
export type DraftStep = Draft['steps'][number];
export type Pick = NonNullable<DraftStep['pick']>;
export type Schedule = AppRouterInputs['automations']['create']['schedule'];
export type Frequency = NonNullable<Schedule['frequency']>;

export function positionsText(indexes: number[]): string {
  return indexes.map((index) => `#${index}`).join(', ');
}

// "1, 2, 5" → [1, 2, 5]; null when it is not a list of positions.
export function parsePositions(text: string): number[] | null {
  if (!POSITIONS.test(text)) return null;
  const indexes = [...new Set(text.split(',').map((part) => Number(part.trim())))];
  if (indexes.some((index) => index < 1 || index > MAX_POSITION)) return null;
  return indexes.sort((a, b) => a - b);
}

// "#1, #2, #5 by recent spend"; the order is the parent's, which published the list picked from.
export function ruleLine(pick: Pick, copy: DraftStep['copy'], orderLabel: string): string {
  const order = orderLabel === '' ? '' : ` by ${orderLabel}`;
  const picked = `${positionsText(pick.indexes)}${order}`;
  if (copy.group_label === '') return picked;
  const groups = pick.group_indexes ? positionsText(pick.group_indexes) : 'All';
  return `${picked} · ${groups} ${copy.group_label}`;
}

// The service's recap for the first step whose rows the next step picked from.
export function recapLine(steps: DraftStep[]): string | null {
  for (const [index, step] of steps.entries()) {
    const next = steps[index + 1];
    if (step.copy.recap === '' || next?.pick == null || step.found === null) continue;
    return step.copy.recap
      .replace('{selected}', String(next.pick.indexes.length))
      .replace('{found}', String(step.found));
  }
  return null;
}

// "08:00" → "8:00 AM"
export function timeLabel(timeOfDay: string): string {
  const [hours = 0, minutes = 0] = timeOfDay.split(':').map(Number);
  const suffix = hours < 12 ? 'AM' : 'PM';
  return `${hours % 12 === 0 ? 12 : hours % 12}:${String(minutes).padStart(2, '0')} ${suffix}`;
}

// "Weekly · Mondays 8:00 AM UTC"; schedules run on UTC, with no per-account zone.
export function scheduleLabel(frequency: Frequency, timeOfDay: string): string {
  const day = FREQUENCY_DAY[frequency];
  const time = `${timeLabel(timeOfDay)} UTC`;
  return `${FREQUENCY_LABEL[frequency]} · ${day === null ? time : `${day} ${time}`}`;
}

export function frequencyLabel(frequency: Frequency): string {
  return FREQUENCY_LABEL[frequency];
}

export function firstRunDate(frequency: Frequency, timeOfDay: string, now = Date.now()): string {
  const [hours = 0, minutes = 0] = timeOfDay.split(':').map(Number);
  const candidate = new Date(now);
  candidate.setUTCHours(hours, minutes, 0, 0);
  while (
    candidate.getTime() <= now ||
    (frequency === 'weekly' && candidate.getUTCDay() !== 1) ||
    (frequency === 'monthly' && (candidate.getUTCDay() !== 1 || candidate.getUTCDate() > 7))
  ) {
    candidate.setUTCDate(candidate.getUTCDate() + 1);
  }
  return candidate.toISOString().slice(0, 10);
}

// "Mon, Oct 26 at 8:00 AM UTC"
export function runAtLabel(at: string | Date): string {
  const date = new Date(at);
  const day = date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
  return `${day} at ${timeLabel(date.toISOString().slice(11, 16))} UTC`;
}

// "Oct 26", in UTC like every schedule time.
export function dayLabel(at: string | Date): string {
  return new Date(at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

// "channel_unavailable:is_archived" → "channel archived"
export function slackReasonLabel(reason: string): string {
  if (reason === 'not_connected') return 'Slack is not connected';
  const [kind, code = ''] = reason.split(':');
  if (kind === 'channel_unavailable')
    return CHANNEL_UNAVAILABLE_LABEL[code] ?? 'channel unavailable';
  return `Slack error (${code || reason})`;
}

export function emailDeliveryLabel(occurrence: Occurrence): string {
  if (!DELIVERED_STATUSES.has(occurrence.status)) return '—';
  const email = occurrence.delivery?.email ?? null;
  return email === null ? 'Not sent' : `Sent ${email.sent} of ${email.total}`;
}

export function slackDeliveryLabel(occurrence: Occurrence): string {
  if (!DELIVERED_STATUSES.has(occurrence.status)) return '—';
  const slack = occurrence.delivery?.slack ?? null;
  if (slack === null) return 'Not sent';
  if (slack.status === 'sent') return 'Sent';
  return slack.reason === null
    ? 'Failed (reason not recorded)'
    : `Failed: ${slackReasonLabel(slack.reason)}`;
}
